import { useState, forwardRef, useEffect, useRef, MouseEvent, useCallback } from "react";
import { createPortal } from "react-dom";
import { PlaylistRecommendationResult, AlbumRecommendation, BuyLinkInfo, TrackInfo } from "../../domain/recommendations";
import { SITE_CONFIG } from "../../app/config";
import { useLanguage } from "../../i18n";
import { Language } from "../../i18n/types";
import { appendCdandlpLanguage } from "../../shared/utils/format";

// Format duration from ms to mm:ss
const formatDuration = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Format total duration (longer format for album totals)
const formatTotalDuration = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
};

// Format price with currency
const formatPrice = (amount: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2
  }).format(amount);
};

// Check if mobile
const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 600);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  
  return isMobile;
};

// Fun loading facts
const LOADING_FACTS = [
  { emoji: "🥁", stat: "2,847", label: "drums destroyed recording vinyl" },
  { emoji: "🎸", stat: "42km", label: "of guitar strings used" },
  { emoji: "💿", stat: "∞", label: "times better than streaming" },
  { emoji: "🎹", stat: "88", label: "keys pressed per second" },
  { emoji: "🎤", stat: "9,001", label: "notes hit perfectly" },
  { emoji: "📀", stat: "3.14", label: "rotations per second" },
  { emoji: "🎧", stat: "120dB", label: "of pure audio bliss" },
  { emoji: "🎵", stat: "440Hz", label: "perfect pitch detected" },
  { emoji: "🔊", stat: "11", label: "on the volume dial" },
  { emoji: "🎻", stat: "1,000", label: "violin strings in harmony" },
];

const FancyLoader = (): JSX.Element => {
  const [factIndex, setFactIndex] = useState(0);
  const [dots, setDots] = useState("");

  useEffect(() => {
    // Rotate facts every 2 seconds
    const factInterval = setInterval(() => {
      setFactIndex((prev) => (prev + 1) % LOADING_FACTS.length);
    }, 2000);

    // Animate dots
    const dotsInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);

    return () => {
      clearInterval(factInterval);
      clearInterval(dotsInterval);
    };
  }, []);

  const currentFact = LOADING_FACTS[factIndex];

  return (
    <div className="fancy-loader">
      {/* Spinning vinyl animation */}
      <div className="loader-vinyl-stack">
        <div className="loader-vinyl v1">
          <div className="loader-vinyl-grooves"></div>
          <div className="loader-vinyl-label">
            <span>💿</span>
          </div>
        </div>
        <div className="loader-vinyl v2">
          <div className="loader-vinyl-grooves"></div>
          <div className="loader-vinyl-label">
            <span>🎵</span>
          </div>
        </div>
        <div className="loader-vinyl v3">
          <div className="loader-vinyl-grooves"></div>
          <div className="loader-vinyl-label">
            <span>🎶</span>
          </div>
        </div>
      </div>

      {/* Loading text */}
      <div className="loader-text">
        <span className="loader-title">SCANNING YOUR TASTE{dots}</span>
        <span className="loader-subtitle">Finding physical gold in your digital library</span>
      </div>

      {/* Fun fact */}
      <div className="loader-fact" key={factIndex}>
        <span className="fact-emoji">{currentFact.emoji}</span>
        <div className="fact-content">
          <span className="fact-stat">{currentFact.stat}</span>
          <span className="fact-label">{currentFact.label}</span>
        </div>
      </div>

      {/* Progress steps */}
      <div className="loader-steps">
        <div className="step active">
          <span className="step-icon">🔍</span>
          <span className="step-text">Reading playlist</span>
        </div>
        <div className="step-connector active"></div>
        <div className="step pulse">
          <span className="step-icon">💿</span>
          <span className="step-text">Matching albums</span>
        </div>
        <div className="step-connector"></div>
        <div className="step">
          <span className="step-icon">🛒</span>
          <span className="step-text">Finding deals</span>
        </div>
      </div>
    </div>
  );
};

type BuyButtonsProps = {
  album: AlbumRecommendation;
};

const getBuyLink = (buyLinks: BuyLinkInfo[], format: string, language: Language): string | null => {
  const link = buyLinks.find(
    (l) => l.vendor.toLowerCase() === "cdandlp" && l.format.toLowerCase() === format.toLowerCase()
  );
  if (!link?.url) return null;
  return appendCdandlpLanguage(link.url, language);
};

const BuyButtons = ({ album }: BuyButtonsProps): JSX.Element => {
  const { t, language } = useLanguage();
  const cdLink = getBuyLink(album.buyLinks, "CD", language);
  const vinylLink = getBuyLink(album.buyLinks, "Vinyl", language);

  if (!cdLink && !vinylLink) {
    return <div className="buy-actions no-links"><span className="no-links-text">{t.results.comingSoon}</span></div>;
  }

  return (
    <div className="buy-actions">
      {vinylLink && (
        <a
          href={vinylLink}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="buy-action-btn vinyl"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="disc-icon-wrap">
            <span className="vinyl-record-icon">
              <span className="vinyl-label"></span>
            </span>
          </span>
          <span className="buy-action-label">
            <span className="buy-action-format">VINYL</span>
            {album.prices.vinyl && (
              <span className="buy-action-price">
                {album.prices.vinyl.source === "estimated" ? "~" : ""}{formatPrice(album.prices.vinyl.amount)}
              </span>
            )}
          </span>
        </a>
      )}
      {cdLink && (
        <a
          href={cdLink}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="buy-action-btn cd"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="disc-icon-wrap">
            <span className="cd-icon">💿</span>
          </span>
          <span className="buy-action-label">
            <span className="buy-action-format">CD</span>
            {album.prices.cd && (
              <span className="buy-action-price">
                {album.prices.cd.source === "estimated" ? "~" : ""}{formatPrice(album.prices.cd.amount)}
              </span>
            )}
          </span>
        </a>
      )}
    </div>
  );
};

type RecommendationsSectionProps = {
  data?: PlaylistRecommendationResult;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
};

type TrackCountBadgeProps = {
  count: number;
};

const TrackCountBadge = ({ count }: TrackCountBadgeProps): JSX.Element => {
  const { t } = useLanguage();
  return (
    <span className="track-count-badge">
      {count} {count === 1 ? t.results.track : t.results.tracks}
    </span>
  );
};

// Album Modal Component
type AlbumModalProps = {
  album: AlbumRecommendation;
  onClose: () => void;
};

const AlbumModal = ({ album, onClose }: AlbumModalProps): JSX.Element => {
  const { t, language } = useLanguage();
  const modalRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const cdLink = getBuyLink(album.buyLinks, "CD", language);
  const vinylLink = getBuyLink(album.buyLinks, "Vinyl", language);
  
  // Calculate total listening time
  const totalDurationMs = album.trackDetails.reduce((acc, track) => acc + track.durationMs, 0);
  const totalDuration = formatTotalDuration(totalDurationMs);

  // Store scroll position for restoration
  const scrollYRef = useRef(0);

  // Close on escape key and handle body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    
    // Store scroll position and lock body
    scrollYRef.current = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.width = "100%";
    
    // Reset modal scroll to top after render
    const timer = setTimeout(() => {
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
      }
    }, 10);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleEscape);
      // Restore body scroll
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      // Use instant scroll to avoid animation when closing modal
      window.scrollTo({ top: scrollYRef.current, left: 0, behavior: "instant" });
    };
  }, [onClose]);

  // Prevent wheel events on backdrop from causing scroll glitches
  const handleBackdropWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    // Only allow scrolling if inside the modal
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Close on backdrop click - prevent event bubbling issues
  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on backdrop, not children
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  // Mobile layout: similar to desktop with vinyl animation
  if (isMobile) {
    return createPortal(
      <div className="album-modal-backdrop" onClick={handleBackdropClick} onWheel={handleBackdropWheel}>
        <div className="album-modal" ref={modalRef}>
          <button type="button" className="modal-close-btn" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close">
            ✕
          </button>
          
          {/* Album Header - Same style as desktop */}
          <div className="modal-album-header mobile">
            <div className="modal-cover-wrap auto-reveal">
              <img src={album.coverUrl} alt={album.name} className="modal-album-cover" />
              <div className="modal-vinyl-disc">
                <div className="vinyl-grooves"></div>
                <div className="vinyl-center"></div>
              </div>
            </div>
            <div className="modal-album-info">
              <h2 className="modal-album-title">{album.name}</h2>
              <p className="modal-album-artist">{album.artist}</p>
              {album.releaseYear && (
                <span className="modal-album-year">{album.releaseYear}</span>
              )}
            </div>
          </div>

          {/* Price Banner */}
          {(album.prices.cd || album.prices.vinyl) && (
            <div className="modal-price-banner">
              <div className="price-banner-title">
                <span className="price-icon">💰</span>
                <span>Own This Album Forever for</span>
              </div>
              <div className="price-options">
                {album.prices.cd && (
                  cdLink ? (
                    <a
                      href={cdLink}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      className="price-option cd clickable"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="price-format-icon">💿</span>
                      <div className="price-details">
                        <span className="price-format">CD</span>
                        <span className="price-amount">{formatPrice(album.prices.cd.amount)}</span>
                        <span className="price-source">
                          {album.prices.cd.source === "discogs" ? "starts from" : "~estimated"}
                        </span>
                      </div>
                    </a>
                  ) : (
                    <div className="price-option cd">
                      <span className="price-format-icon">💿</span>
                      <div className="price-details">
                        <span className="price-format">CD</span>
                        <span className="price-amount">{formatPrice(album.prices.cd.amount)}</span>
                        <span className="price-source">
                          {album.prices.cd.source === "discogs" ? "starts from" : "~estimated"}
                        </span>
                      </div>
                    </div>
                  )
                )}
                {album.prices.vinyl && (
                  vinylLink ? (
                    <a
                      href={vinylLink}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      className="price-option vinyl clickable"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="price-format-icon vinyl-icon">
                        <span className="vinyl-record-icon small">
                          <span className="vinyl-label"></span>
                        </span>
                      </span>
                      <div className="price-details">
                        <span className="price-format">VINYL</span>
                        <span className="price-amount">{formatPrice(album.prices.vinyl.amount)}</span>
                        <span className="price-source">
                          {album.prices.vinyl.source === "discogs" ? "starts from" : "~estimated"}
                        </span>
                      </div>
                    </a>
                  ) : (
                    <div className="price-option vinyl">
                      <span className="price-format-icon vinyl-icon">
                        <span className="vinyl-record-icon small">
                          <span className="vinyl-label"></span>
                        </span>
                      </span>
                      <div className="price-details">
                        <span className="price-format">VINYL</span>
                        <span className="price-amount">{formatPrice(album.prices.vinyl.amount)}</span>
                        <span className="price-source">
                          {album.prices.vinyl.source === "discogs" ? "starts from" : "~estimated"}
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
              <span className="price-disclaimer">*Prices may vary. Click to see latest deals!</span>
            </div>
          )}

          {/* Your Tracks Section */}
          <div className="modal-tracks-section">
            <h3 className="modal-section-title">
              <span className="section-icon">💿</span>
              Your Tracks
            </h3>
            <div className="modal-track-list">
              {album.trackDetails.length > 0 ? (
                album.trackDetails.map((track: TrackInfo, index: number) => (
                  <div key={track.id} className="modal-track-item highlighted">
                    <span className="track-number">{index + 1}</span>
                    <div className="track-info">
                      <span className="track-name">{track.name}</span>
                    </div>
                    <span className="track-heart">❤️</span>
                  </div>
                ))
              ) : (
                album.tracks.map((trackName: string, index: number) => (
                  <div key={trackName} className="modal-track-item highlighted">
                    <span className="track-number">{index + 1}</span>
                    <div className="track-info">
                      <span className="track-name">{trackName}</span>
                    </div>
                    <span className="track-heart">❤️</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Album Info */}
          <div className="modal-album-stats-section">
            <h3 className="modal-section-title">
              <span className="section-icon">📀</span>
              Album Info
            </h3>
            <div className="modal-stats-grid">
              <div className="modal-stat-card">
                <span className="stat-card-icon">🎵</span>
                <span className="stat-card-value">{album.trackCount}</span>
                <span className="stat-card-label">Your {album.trackCount === 1 ? "Track" : "Tracks"}</span>
              </div>
              <div className="modal-stat-card">
                <span className="stat-card-icon">⏱️</span>
                <span className="stat-card-value">{totalDuration}</span>
                <span className="stat-card-label">Listening Time</span>
              </div>
              {album.releaseYear && (
                <div className="modal-stat-card">
                  <span className="stat-card-icon">📅</span>
                  <span className="stat-card-value">{album.releaseYear}</span>
                  <span className="stat-card-label">Released</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Desktop layout
  return createPortal(
    <div className="album-modal-backdrop" onClick={handleBackdropClick} onWheel={handleBackdropWheel}>
      <div className="album-modal" ref={modalRef}>
        <button type="button" className="modal-close-btn" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close">
          ✕
        </button>
        
        {/* Album Header */}
        <div className="modal-album-header">
          <div className="modal-cover-wrap">
            <img src={album.coverUrl} alt={album.name} className="modal-album-cover" />
            <div className="modal-vinyl-disc">
              <div className="vinyl-grooves"></div>
              <div className="vinyl-center"></div>
            </div>
          </div>
          <div className="modal-album-info">
            <h2 className="modal-album-title">{album.name}</h2>
            <p className="modal-album-artist">{album.artist}</p>
            {album.releaseYear && (
              <span className="modal-album-year">{album.releaseYear}</span>
            )}
            
            {/* Stats Grid */}
            <div className="modal-stats-grid">
              <div className="modal-stat-card">
                <span className="stat-card-icon">🎵</span>
                <span className="stat-card-value">{album.trackCount}</span>
                <span className="stat-card-label">Your {album.trackCount === 1 ? "Track" : "Tracks"}</span>
              </div>
              <div className="modal-stat-card">
                <span className="stat-card-icon">⏱️</span>
                <span className="stat-card-value">{totalDuration}</span>
                <span className="stat-card-label">Listening Time</span>
              </div>
            </div>
          </div>
        </div>

        {/* Price Banner */}
        {(album.prices.cd || album.prices.vinyl) && (
          <div className="modal-price-banner">
            <div className="price-banner-title">
              <span className="price-icon">💰</span>
              <span>Own This Album Forever for</span>
            </div>
            <div className="price-options">
              {album.prices.cd && (
                cdLink ? (
                  <a
                    href={cdLink}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="price-option cd clickable"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="price-format-icon">💿</span>
                    <div className="price-details">
                      <span className="price-format">CD</span>
                      <span className="price-amount">{formatPrice(album.prices.cd.amount)}</span>
                      <span className="price-source">
                        {album.prices.cd.source === "discogs" ? "starts from" : "~estimated"}
                      </span>
                    </div>
                  </a>
                ) : (
                  <div className="price-option cd">
                    <span className="price-format-icon">💿</span>
                    <div className="price-details">
                      <span className="price-format">CD</span>
                      <span className="price-amount">{formatPrice(album.prices.cd.amount)}</span>
                      <span className="price-source">
                        {album.prices.cd.source === "discogs" ? "starts from" : "~estimated"}
                      </span>
                    </div>
                  </div>
                )
              )}
              {album.prices.vinyl && (
                vinylLink ? (
                  <a
                    href={vinylLink}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="price-option vinyl clickable"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="price-format-icon vinyl-icon">
                      <span className="vinyl-record-icon small">
                        <span className="vinyl-label"></span>
                      </span>
                    </span>
                    <div className="price-details">
                      <span className="price-format">VINYL</span>
                      <span className="price-amount">{formatPrice(album.prices.vinyl.amount)}</span>
                      <span className="price-source">
                        {album.prices.vinyl.source === "discogs" ? "starts from" : "~estimated"}
                      </span>
                    </div>
                  </a>
                ) : (
                  <div className="price-option vinyl">
                    <span className="price-format-icon vinyl-icon">
                      <span className="vinyl-record-icon small">
                        <span className="vinyl-label"></span>
                      </span>
                    </span>
                    <div className="price-details">
                      <span className="price-format">VINYL</span>
                      <span className="price-amount">{formatPrice(album.prices.vinyl.amount)}</span>
                      <span className="price-source">
                        {album.prices.vinyl.source === "discogs" ? "starts from" : "~estimated"}
                      </span>
                    </div>
                  </div>
                )
              )}
            </div>
            <span className="price-disclaimer">* Prices may vary. Click to see latest deals!</span>
          </div>
        )}

        {/* Your Tracks Section */}
        <div className="modal-tracks-section">
          <h3 className="modal-section-title">
            <span className="section-icon">💿</span>
            Your Tracks From This Album
          </h3>
          <div className="modal-track-list">
            {album.trackDetails.length > 0 ? (
              album.trackDetails.map((track: TrackInfo, index: number) => (
                <div key={track.id} className="modal-track-item highlighted">
                  <span className="track-number">{index + 1}</span>
                  <div className="track-info">
                    <span className="track-name">{track.name}</span>
                    {track.artist !== album.artist && (
                      <span className="track-artist">feat. {track.artist}</span>
                    )}
                  </div>
                  <span className="track-duration">{formatDuration(track.durationMs)}</span>
                  <span className="track-heart">❤️</span>
                </div>
              ))
            ) : (
              album.tracks.map((trackName: string, index: number) => (
                <div key={trackName} className="modal-track-item highlighted">
                  <span className="track-number">{index + 1}</span>
                  <div className="track-info">
                    <span className="track-name">{trackName}</span>
                  </div>
                  <span className="track-heart">❤️</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Why Own Physical */}
        <div className="modal-why-physical">
          <div className="why-physical-content">
            <span className="why-physical-icon">🔒</span>
            <div className="why-physical-text">
              <span className="why-physical-title">No subscriptions. No disappearing tracks.</span>
              <span className="why-physical-desc">Own your music forever.</span>
            </div>
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
};

const ShareButton = ({ playlistId, playlistName }: { playlistId: string; playlistName: string }): JSX.Element => {
  const { t } = useLanguage();
  const [state, setState] = useState<"idle" | "copied" | "shared">("idle");

  const getShareUrl = () => {
    const url = new URL(window.location.origin);
    url.searchParams.set("playlist", playlistId);
    return url.toString();
  };

  const handleShare = async () => {
    const shareUrl = getShareUrl();
    const shareText = t.share.shareText.replace("{playlistName}", playlistName);
    const shareData = {
      title: `${playlistName} - ${SITE_CONFIG.name}`,
      text: shareText,
      url: shareUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setState("shared");
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setState("copied");
      }
      setTimeout(() => setState("idle"), 2500);
    } catch {
      await navigator.clipboard.writeText(shareUrl);
      setState("copied");
      setTimeout(() => setState("idle"), 2500);
    }
  };

  const getText = () => {
    if (state === "copied") return t.share.linkCopied;
    if (state === "shared") return t.share.shared;
    return t.share.shareHaul;
  };

  return (
    <button
      type="button"
      className={`viral-share-btn ${state !== "idle" ? "success" : ""}`}
      onClick={handleShare}
      aria-label="Share this haul"
    >
      <span className="share-text">{getText()}</span>
      <span className="share-icon">→</span>
    </button>
  );
};

export const RecommendationsSection = forwardRef<HTMLDivElement, RecommendationsSectionProps>(({
  data,
  isLoading,
  isError,
  errorMessage
}, ref): JSX.Element | null => {
  const { t } = useLanguage();
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumRecommendation | null>(null);
  const hasAlbums = Boolean(data?.albums?.length);
  const hasSearched = Boolean(data);

  if (isError) {
    return (
      <section ref={ref} className="box results-box">
        <div className="receipt-header" style={{ background: "#d63031" }}>
          <div className="receipt-title">{t.results.oops}</div>
        </div>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p style={{ fontSize: "1.2rem", fontWeight: 700 }}>{errorMessage ?? t.results.unableToProcess}</p>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section ref={ref} className="box results-box loading-state">
        <FancyLoader />
      </section>
    );
  }

  // Searched but no albums found
  if (hasSearched && !hasAlbums) {
    return (
      <section ref={ref} className="box results-box">
        <div className="receipt-header" style={{ background: "#636e72" }}>
          <div className="receipt-title">{t.results.noAlbumsFound}</div>
          <div className="receipt-meta">
            {t.results.playlist}: {data?.playlistName}
          </div>
        </div>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p style={{ fontSize: "3rem", marginBottom: "1rem" }}>🤷</p>
          <p style={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: "0.5rem" }}>
            {t.results.noAlbumsDesc}
          </p>
          <p style={{ opacity: 0.8 }}>
            {t.results.tryDifferent}
          </p>
        </div>
      </section>
    );
  }

  // Haven't searched yet
  if (!hasSearched) {
    return null;
  }

  return (
    <section ref={ref} className="box results-box">
      {/* Receipt Header */}
      <div className="receipt-header-new">
        <div className="receipt-stats-left">
          <div className="receipt-stat-pill">
            <span className="stat-icon">🎵</span>
            <span className="stat-number">{data?.trackCount}</span>
            <span className="stat-label">{t.results.tracks}</span>
          </div>
          <span className="receipt-arrow">→</span>
          <div className="receipt-stat-pill highlight">
            <span className="stat-icon">💿</span>
            <span className="stat-number">{data?.albumCount}</span>
            <span className="stat-label">albums</span>
          </div>
        </div>
        <div className="receipt-title-right">
          <div className="receipt-title">{t.results.yourHaul}</div>
          <h2 className="receipt-playlist-name">"{data?.playlistName}"</h2>
        </div>
      </div>

      {/* VIRAL VALUE SECTION - THE MONEY SHOT */}
      {data && (data.totals.cd > 0 || data.totals.vinyl > 0) && (
        <div className="value-section-viral">
          <div className="value-question">
            <span className="value-question-text">HOW MUCH IS YOUR PLAYLIST?!</span>
            <span className="value-question-tag">💰</span>
          </div>
          <div className="value-prices-row">
            {data.totals.cd > 0 && (
              <div className="value-price-card cd">
                <div className="price-card-icon-wrap">
                  <span className="price-card-icon">💿</span>
                  <span className="price-card-shine"></span>
                </div>
                <div className="price-card-content">
                  <span className="price-card-format">CD COLLECTION</span>
                  <span className="price-card-amount">~{formatPrice(data.totals.cd)}</span>
                  <span className="price-card-estimate">*estimated price</span>
                </div>
              </div>
            )}
            {data.totals.vinyl > 0 && (
              <div className="value-price-card vinyl">
                <div className="price-card-icon-wrap">
                  <span className="price-card-icon">🎵</span>
                  <span className="price-card-shine"></span>
                </div>
                <div className="price-card-content">
                  <span className="price-card-format">VINYL COLLECTION</span>
                  <span className="price-card-amount">~{formatPrice(data.totals.vinyl)}</span>
                  <span className="price-card-estimate">*estimated price</span>
                </div>
              </div>
            )}
          </div>
          <div className="value-share-row">
            <span className="value-share-text">Own your music forever. Share the flex! 🔥</span>
            <ShareButton playlistId={data.playlistId} playlistName={data.playlistName} />
          </div>
        </div>
      )}

      <div className="affiliate-notice-compact">
        <span className="affiliate-compact-icon">💡</span>
        <span className="affiliate-compact-text">{t.affiliate.disclosure} Links support us at no extra cost!</span>
      </div>

      <div className="album-list">
        {data?.albums.map((album) => (
          <div 
            key={album.id} 
            className="album-row clickable"
            onClick={(e) => {
              // Prevent click from propagating and triggering multiple times
              e.preventDefault();
              e.stopPropagation();
              setSelectedAlbum(album);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setSelectedAlbum(album);
              }
            }}
          >
            <div className="album-cover-wrap">
              <img 
                src={album.coverUrl} 
                alt={album.name}
                className="album-cover"
              />
              <TrackCountBadge count={album.trackCount} />
            </div>
            <div className="album-info">
              <h4 className="album-title">{album.name}</h4>
              <span className="album-artist">{album.artist}</span>
              <span className="album-tracks">
                {album.tracks.join(', ')}
              </span>
            </div>
            <BuyButtons album={album} />
          </div>
        ))}
      </div>

      {/* Album Modal */}
      {selectedAlbum && (
        <AlbumModal album={selectedAlbum} onClose={() => setSelectedAlbum(null)} />
      )}

      <div className="receipt-footer">
        <div className="receipt-total">
          <span className="total-label">{t.results.albumsToOwn}</span>
          <span className="total-amount">{data?.albumCount}</span>
        </div>
        {data && (
          <ShareButton playlistId={data.playlistId} playlistName={data.playlistName} />
        )}
      </div>
    </section>
  );
});

RecommendationsSection.displayName = "RecommendationsSection";

